<template>
    <div class="flex flex-col gap-4">
        <page-header>
            Sessions

            <template v-if="!isImpersonating" v-slot:actions>
                <vx-button
                    color="danger"
                    outline
                    @click.prevent="signOutEverywhereElse"
                    :loading="submitting"
                    data-test="sign-out-everywhere-else"
                >
                    Sign out everywhere else
                    <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <vx-alert v-if="success" :closeable="false" color="success" shadow> {{ success }} </vx-alert>

        <vx-expandable-card>
            <template v-slot:header>Web Sessions</template>
            <template v-slot:content>
                <page-loading v-if="loading && !sessions.length"></page-loading>

                <vx-table v-else-if="sessions.length" class="xl:grid-cols-4" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">Browser</div>
                        <div>IP Address</div>
                        <div>Last Seen</div>
                        <div></div>
                    </vx-table-row>

                    <vx-table-row v-for="session in sessions" :key="session.id" data-test="session">
                        <vx-table-row-title>
                            {{ userAgentLabel(session.user_agent) }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> IP Address: </template>
                            <vx-popover v-if="session.ip_address" hover arrow placement="top" class="w-full">
                                <button
                                    type="button"
                                    class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    data-test="ip-address"
                                >
                                    <span class="block truncate">{{ session.ip_address }}</span>
                                </button>
                                <template v-slot:content>
                                    <code class="text-xs">{{ session.ip_address }}</code>
                                </template>
                            </vx-popover>
                            <template v-else>-</template>
                        </vx-table-row-item>

                        <vx-table-row-item data-test="last-seen-cell">
                            <template v-slot:label> Last Seen: </template>
                            <vx-popover hover arrow placement="top">
                                <button
                                    type="button"
                                    class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                    data-test="last-seen"
                                >
                                    <span :key="dateTimeKey">{{ fromNow(session.last_seen_at) }}</span>
                                </button>
                                <template v-slot:content>
                                    {{ shortDateTime(session.last_seen_at) }}
                                </template>
                            </vx-popover>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <span v-if="session.current" class="text-sm text-slate-500" data-test="current"
                                >This session</span
                            >
                            <vx-button
                                v-else-if="!isImpersonating"
                                size="sm"
                                color="muted"
                                outline
                                @click="end(session)"
                                data-test="end"
                            >
                                End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                            </vx-button>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div v-else class="text-sm text-slate-500" data-test="no-sessions">No web sessions.</div>
            </template>
        </vx-expandable-card>

        <vx-expandable-card>
            <template v-slot:header>Mobile Devices</template>
            <template v-slot:content>
                <page-loading v-if="loading && !devices.length"></page-loading>

                <vx-table v-else-if="devices.length" class="xl:grid-cols-4" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">Device</div>
                        <div>Registered</div>
                        <div>Last Used</div>
                        <div></div>
                    </vx-table-row>

                    <vx-table-row v-for="device in devices" :key="device.id" data-test="device">
                        <vx-table-row-title>
                            {{ mobileDeviceLabel(device) }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> Registered: </template>
                            <vx-popover hover arrow placement="top">
                                <button
                                    type="button"
                                    class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                >
                                    <span :key="dateTimeKey">{{ fromNow(device.created_at) }}</span>
                                </button>
                                <template v-slot:content>
                                    {{ shortDateTime(device.created_at) }}
                                </template>
                            </vx-popover>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Last Used: </template>
                            <vx-popover hover arrow placement="top">
                                <button
                                    type="button"
                                    class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                >
                                    <span :key="dateTimeKey">{{ fromNow(device.updated_at) }}</span>
                                </button>
                                <template v-slot:content>
                                    {{ shortDateTime(device.updated_at) }}
                                </template>
                            </vx-popover>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <vx-button
                                v-if="!isImpersonating"
                                size="sm"
                                color="muted"
                                outline
                                @click="endDevice(device)"
                                data-test="end-device"
                            >
                                End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                            </vx-button>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div v-else class="text-sm text-slate-500" data-test="no-devices">No mobile devices.</div>
            </template>
        </vx-expandable-card>

        <vx-expandable-card>
            <template v-slot:header>Connected Apps</template>
            <template v-slot:content>
                <page-loading v-if="loading && !apps.length"></page-loading>

                <vx-table v-else-if="apps.length" class="xl:grid-cols-5" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div class="lg:pl-6">App</div>
                        <div>Team</div>
                        <div>Connected</div>
                        <div>Last Used</div>
                        <div></div>
                    </vx-table-row>

                    <vx-table-row v-for="app in apps" :key="app.id" data-test="app">
                        <vx-table-row-title>
                            {{ app.client_host }}
                        </vx-table-row-title>

                        <vx-table-row-item>
                            <template v-slot:label> Team: </template>
                            {{ app.team_name ?? '-' }}
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Connected: </template>
                            <vx-popover hover arrow placement="top">
                                <button
                                    type="button"
                                    class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                >
                                    <span :key="dateTimeKey">{{ fromNow(app.created_at) }}</span>
                                </button>
                                <template v-slot:content>
                                    {{ shortDateTime(app.created_at) }}
                                </template>
                            </vx-popover>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <template v-slot:label> Last Used: </template>
                            <vx-popover hover arrow placement="top">
                                <button
                                    type="button"
                                    class="focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                                >
                                    <span :key="dateTimeKey">{{ fromNow(app.last_used_at) }}</span>
                                </button>
                                <template v-slot:content>
                                    {{ shortDateTime(app.last_used_at) }}
                                </template>
                            </vx-popover>
                        </vx-table-row-item>

                        <vx-table-row-item>
                            <vx-button
                                v-if="!isImpersonating"
                                size="sm"
                                color="muted"
                                outline
                                @click="endApp(app)"
                                data-test="end-app"
                            >
                                End <font-awesome-icon widthAuto :icon="faRightFromBracket"></font-awesome-icon>
                            </vx-button>
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>

                <div v-else class="text-sm text-slate-500" data-test="no-apps">No connected apps.</div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import axios from '~/services/axios';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxExpandableCard,
    VxPopover,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { fromNow, shortDateTime } from '~/utils/date';
import { mobileDeviceLabel } from '~/utils/mobileDevice';
import { throwSwalRequestError } from '~/utils/request';
import { userAgentLabel } from '~/utils/userAgent';

interface ProfileSession {
    id: number;
    user_id: number;
    impersonator_id: number | null;
    current_team_id: number | null;
    method: 'password' | 'sso' | 'impersonation';
    remembered: boolean;
    ip_address: string | null;
    user_agent: string | null;
    last_seen_at: string;
    ended_at: string | null;
    current: boolean;
    created_at: string;
    updated_at: string;
}

interface ProfileMobileDevice {
    id: string;
    type: 'android' | 'ios';
    name: string;
    current_team_id: number;
    created_at: string;
    updated_at: string;
}

interface ProfileConnectedApp {
    id: string;
    client_host: string;
    team_id: number;
    team_name: string | null;
    created_at: string;
    last_used_at: string;
}

const toasted = useToasted();
const isImpersonating = Spark.isImpersonating;
const loading = ref(true);
const submitting = ref(false);
const success = ref('');
const sessions = ref<ProfileSession[]>([]);
const devices = ref<ProfileMobileDevice[]>([]);
const apps = ref<ProfileConnectedApp[]>([]);
const dateTimeKey = useInterval(60000);

const load = async () => {
    loading.value = true;

    try {
        const { data } = await axios.get('/settings/sessions');
        sessions.value = data.sessions;
        devices.value = data.devices;
        apps.value = data.apps;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const end = (session: ProfileSession) => {
    VxSwal.confirmation(
        'End session',
        'That browser is signed out within a minute, or at its next visit if it is idle, and will need to log in again. There is no undo.',
        'danger',
        () =>
            axios
                .delete(`/settings/sessions/${session.id}`)
                .then(() => {
                    success.value = 'Session signed out';
                    load();
                })
                .catch(throwSwalRequestError)
    );
};

const endDevice = (device: ProfileMobileDevice) => {
    VxSwal.confirmation(
        'End device session',
        'The app on that phone is signed out within around a minute, or at its next use if it is idle, and will need to log in again. There is no undo.',
        'danger',
        () =>
            axios
                .delete(`/settings/mobile-devices/${device.id}`)
                .then(() => {
                    success.value = 'Device signed out';
                    load();
                })
                .catch(throwSwalRequestError)
    );
};

const endApp = (app: ProfileConnectedApp) => {
    VxSwal.confirmation(
        'End connection',
        'The app loses access within around a minute and must be connected again. There is no undo.',
        'danger',
        () =>
            axios
                .delete(`/settings/connected-apps/${app.id}`)
                .then(() => {
                    success.value = 'App disconnected';
                    load();
                })
                .catch(throwSwalRequestError)
    );
};

const signOutEverywhereElse = () => {
    VxSwal.confirmation(
        'Sign out everywhere else',
        'Every other browser and every phone is signed out and every connected app is disconnected within around a minute, and every browser that chose Remember me must log in again. This browser stays signed in.',
        'danger',
        () => {
            submitting.value = true;

            return axios
                .post('/settings/sessions/sign-out-everywhere-else')
                .then(() => {
                    success.value = 'Signed out everywhere else';
                    load();
                })
                .catch(throwSwalRequestError)
                .finally(() => {
                    submitting.value = false;
                });
        }
    );
};

load();
</script>
